/**
 * Debounce for promises.
 *
 * @typeParam T - The async function type (infered)
 *
 * @param fn The async function to debounce
 * @param ms Time in milliseconds
 *
 * @example
 * ```ts
 * const debounced = debouncePromise(async (value: string) => {
 * 	// some async code
 * }, 300);
 * ```
 */
export const debouncePromise = <
	T extends (...args: any[]) => Promise<any> | void,
>(
	fn: T,
	ms = 0,
) => {
	let timeoutId: NodeJS.Timeout;
	const pending: {
		resolve(value?: any): void;
		reject(reason?: any): void;
	}[] = [];
	return (...args: Parameters<T>): ReturnType<T> =>
		new Promise((resolve, reject) => {
			clearTimeout(timeoutId);
			timeoutId = setTimeout(() => {
				const currentPending = [...pending];
				pending.length = 0;
				Promise.resolve(fn.apply(this, args)).then(
					(data) => currentPending.forEach(({resolve}) => resolve(data)),
					(error) => currentPending.forEach(({reject}) => reject(error)),
				);
			}, ms);
			pending.push({resolve, reject});
		}) as ReturnType<T>;
};
